import { useTranslation } from "react-i18next";

import { PrecisionNote } from "@/components/ui/precision-note";
import { cn } from "@/lib/utils/cn";

import type { ScopeFacet } from "../../types";

/**
 * Service-scope display: each facet lists every reference
 * option — chosen options render green, the rest red — and a fully-selected
 * facet collapses to a single green "all" chip. Generous spacing between
 * facets keeps the categories legible. Shared by the expert dashboard and the
 * CQDD admin review so both render the coverage identically.
 */
export function ScopeFacets({ facets }: { facets: ScopeFacet[] }) {
  return (
    <div className="space-y-6">
      {facets.map((facet) => (
        <div key={facet.label} className="space-y-2">
          <p className="text-sm font-semibold">{facet.label}</p>
          <div className="flex flex-wrap gap-1.5">
            {facet.all_selected ? (
              <Chip label={facet.all_label} chosen />
            ) : (
              // Option names aren't unique (e.g. two same-named regions), so
              // key on the index within this facet's stable, ordered list.
              facet.options.map((option, index) => (
                <Chip
                  key={`${facet.label}-${index}`}
                  label={option.name}
                  chosen={option.chosen}
                  added={option.added}
                  removed={option.removed}
                />
              ))
            )}
          </div>
          {/* Free text, not a catalogue option — a labelled line, never a chip. */}
          <PrecisionNote text={facet.other} />
        </div>
      ))}
    </div>
  );
}

/** A pending change's additions and removals are marked as pistes d'action
 *  are on the review page, as the Django facets mark them. */
function Chip({
  label,
  chosen,
  added,
  removed,
}: {
  label: string;
  chosen: boolean;
  added?: boolean;
  removed?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center whitespace-normal break-words rounded-full px-2.5 py-0.5 text-left text-xs font-medium",
        chosen
          ? "bg-success text-success-foreground"
          : "border border-destructive/40 text-destructive",
      )}
    >
      {removed ? (
        <>
          <del>{label}</del>&nbsp;· {t("expert.admin.detail.removed")}
        </>
      ) : (
        label
      )}
      {added && <>&nbsp;· {t("expert.admin.detail.isNew")}</>}
    </span>
  );
}
